<template>
  <div class="page-section">
    <h1 class="section-title">
      Last 4 months cash flow
    </h1>
    <p class="section-subtitle">
      These are the last 4 months' bottom lines - your ins & outs combined.
    </p>
    <div
      v-if="isDataMissing"
      class="connect-another-subtitle"
    >
      Unlock your cashflow summary by
      <br>
      <b @click="handleConnectAccountsClick">connecting a more active account</b>
    </div>
    <horizontal-scroll>
      <div
        v-for="(month, index) in displayMonths"
        :key="index"
        class="card"
        :class="{ 'disabled': isDataMissing }"
      >
        <div class="month-header">
          <div class="month-name">
            {{ month.monthName }}
          </div>
          <div :class="['month-balance', month.netAmount >= 0 ? 'positive' : 'negative']">
            {{ formatCurrency(month.netAmount) }}
          </div>
        </div>

        <div class="bars-container">
          <div class="bar-container">
            <div
              class="bar income"
              :style="{ height: month.inBarHeight + 'px' }"
            />
            <div class="amount-container">
              <span class="amount-label positive">In</span>
              <span class="amount-value">{{ isDataMissing ? '-' : '£' + numberToHumanReadable(month.in) }}</span>
            </div>
          </div>
          <div class="bar-container">
            <div
              class="bar expense"
              :style="{ height: month.outBarHeight + 'px' }"
            />
            <div class="amount-container">
              <span class="amount-label negative">Out</span>
              <span class="amount-value">{{ isDataMissing ? '-' : '£' + numberToHumanReadable(month.out) }}</span>
            </div>
          </div>
        </div>
      </div>
    </horizontal-scroll>
    <div class="divider-line" />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { numberToHumanReadable } from '../../../../utils/miscUtils';
import HorizontalScroll from '../../../Common/HorizontalScroll.vue';

const props = defineProps({
  lastFourMonths: {
    type: Array,
    required: true,
  },
  isDataMissing: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['connectAccountsClick']);

const displayMonths = computed(() => {
  if (props.isDataMissing) {
    return [
      {
        monthName: 'September',
        netAmount: 0,
        in: 0,
        out: 0,
        inBarHeight: 100,
        outBarHeight: 100,
      },
      {
        monthName: 'October',
        netAmount: 0,
        in: 0,
        out: 0,
        inBarHeight: 100,
        outBarHeight: 100,
      },
    ];
  }

  return props.lastFourMonths;
});

const formatCurrency = amount => {
  if (props.isDataMissing) {
    return '-';
  }

  if (amount >= 0) {
    return `£${numberToHumanReadable(amount)}`;
  }

  return `£-${numberToHumanReadable(Math.abs(amount))}`;
};

const handleConnectAccountsClick = () => {
  emit('connectAccountsClick');
};
</script>

<style scoped>
.page-section {
  color: white;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding-bottom: 20px;
}

.connect-another-subtitle {
  font-size: 14px;
  font-family: CircularStdBook;
  margin-bottom: 10px;
  margin-top: 10px;
  background-color: #FFDC73;
  color: #205441;
  text-align: center;
  padding: 20px;
  border-radius: 16px;
  width: 90%;
  margin-left: auto;
  margin-right: auto;
  font-weight: 100 !important;
}

.connect-another-subtitle b {
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
}

.section-title {
  font-size: 48px;
  font-weight: bold;
  font-family: CircularStd;
  line-height: 1;
  margin-top: 30px;
  text-align: left;
  width: 90%;
  margin-left: auto;
  margin-right: auto;
}

.section-subtitle {
  font-size: 19px;
  font-family: CircularStdBook;
  margin-top: 40px;
  margin-bottom: 40px;
  text-align: left;
  width: 90%;
  margin-left: auto;
  margin-right: auto;
}

.card {
  flex: 0 0 250px;
  min-width: 250px;
  max-width: 250px;
  width: 250px;
  background-color: white;
  border-radius: 25px;
  padding: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  height: 330px;
  display: flex;
  flex-direction: column;
}

.card.disabled {
  background-color: #F1F5FA !important;
  box-shadow: 0px 16px 46px rgba(22.80, 123.92, 42.57, 0.1) !important;
}

.card.disabled .month-name,
.card.disabled .month-balance,
.card.disabled .amount-value {
  color: #B0B0B0 !important;
}

.month-header {
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-bottom: 16px;
  min-height: 60px;
}

.month-name {
  font-size: 20px;
  font-weight: 700;
  color: #205441;
  margin-bottom: 8px;
}

.month-balance {
  font-size: 18px;
  font-weight: 450;
}

.positive {
  color: #13C636;
}

.negative {
  color: #FF67A1;
}

.bars-container {
  display: flex;
  flex: 1;
  justify-content: space-around;
  align-items: flex-end;
  height: 200px;
  margin-bottom: 5px;
}

.bar-container {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: 100%;
}

.bar {
  width: 87px;
  border-radius: 10px 10px 0 0;
}

.bar.income {
  background-color: #13C636;
}

.bar.expense {
  background-color: #f56c8d;
}

.card.disabled .bar.income {
  background-color: #B8E7C1;
}

.card.disabled .bar.expense {
  background-color: #FFB8D1;
}

.amount-container {
  margin-top: 10px;
  display: flex;
  justify-content: space-around;
  min-height: 40px;
}

.amount-label {
  font-size: 15px;
  margin-bottom: 5px;
}

.amount-value {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.divider-line {
  width: 90%;
  margin: 0 auto;
  margin-top: 30px;
  height: 1px;
  background-color: white;
  opacity: 0.5;
}
</style>
